import { useIsApiReady } from "@/hooks/use-api-ready";
import {
  FILE_CATEGORY_TO_SOURCE_MAP,
  type FileSourceFilter,
} from "@/lib/file-labels";
import {
  fileControllerCreateFile,
  fileControllerGetFiles,
  libraryFileControllerConfirmFileUpload,
} from "@/lib/gen-api";
import { queryKeys } from "@/lib/query-keys";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";

export const LIBRARY_FILE_ALLOWED_MIME_TYPES = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/gif",
  "image/jpeg",
  "image/png",
  "image/webp",
  "text/csv",
  "text/markdown",
  "text/plain",
]);

export type FilesFilters = {
  search: string;
  category: FileSourceFilter;
};

const DEFAULT_FILTERS: FilesFilters = {
  search: "",
  category: null,
};

export function useLibraryFilesData(
  groupUuid: string | null,
  kitUuid: string | null,
  filters: FilesFilters = DEFAULT_FILTERS,
) {
  const queryClient = useQueryClient();
  const isApiReady = useIsApiReady();

  const query = useInfiniteQuery({
    enabled: isApiReady && !!groupUuid && !!kitUuid,
    queryKey: queryKeys.files({
      groupUuid: groupUuid!,
      kitUuid: kitUuid!,
      isLibrary: true,
      ...filters,
    }),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      fileControllerGetFiles({
        query: {
          filter: {
            kitUuid: kitUuid!,
            isLibrary: true,
            sources: filters.category
              ? FILE_CATEGORY_TO_SOURCE_MAP[filters.category]
              : undefined,
            search: filters.search || undefined,
            cursor: pageParam,
          },
        },
      }).then(
        (res) =>
          res.data ?? { data: [], total: 0, offset: 0, nextCursor: null },
      ),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  const libraryFiles = query.data?.pages.flatMap((page) => page.data) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  const create = async (
    name: string,
  ): Promise<{ uuid: string; signedUrl: string }> => {
    if (!groupUuid || !kitUuid) {
      throw new Error("Group UUID and Kit UUID are required");
    }
    const createResponse = await fileControllerCreateFile({
      body: {
        name,
        isLibrary: true,
        isKnowledge: false,
        groupUuid: groupUuid,
        kitUuid: kitUuid,
      },
    });
    const record = createResponse.data;
    if (!record) throw new Error("Failed to create library file record");
    return { uuid: record.uuid, signedUrl: record.signedUrl };
  };

  const confirm = async (fileUuid: string): Promise<void> => {
    if (!groupUuid || !kitUuid) {
      throw new Error("Group UUID and Kit UUID are required");
    }
    await libraryFileControllerConfirmFileUpload({
      throwOnError: true,
      path: { fileUuid },
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.files(),
    });
  };

  return {
    libraryFiles,
    total,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
    create,
    confirm,
  };
}
